import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  flexRender,
  useTable,
  type ColumnDef,
  type ColumnOrderState,
  type ColumnSizingState,
  type ColumnVisibilityState,
  type RowData,
  type SortingState,
  type Updater,
} from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Columns3,
  FilterX,
  LayoutList,
  MoreHorizontal,
  RefreshCw,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  useDataTableChromeHost,
  useDataTableLayoutHost,
  useDeskFilterReset,
} from "@/modules/shell/DataTableChrome";
import {
  deskTableFeatures,
  type DeskTableFeatures,
} from "./data-table-features";
import { alignClass, columnAlign, columnShareStyle } from "./column-meta";
import {
  clearDataTablePrefs,
  loadDataTablePrefs,
  resolveShowRelativeTime,
  resolveViewMode,
  saveDataTablePrefs,
  type DataTableViewMode,
} from "./data-table-prefs";
import { useContainerNarrow } from "./use-container-narrow";

const ShowRelativeTimeContext = createContext(true);

/** Whether Date column cells should show muted time-ago — plan/35. */
export function useDataTableShowRelativeTime(): boolean {
  return useContext(ShowRelativeTimeContext);
}

export type DeskColumnDef<TData extends RowData> = ColumnDef<
  DeskTableFeatures,
  TData
>;

type DataTableProps<TData extends RowData> = {
  listId: string;
  companySlug: string;
  columns: DeskColumnDef<TData>[];
  data: TData[];
  /** Default visible columns — omit id → visible. */
  defaultVisibility?: ColumnVisibilityState;
  defaultOrder?: string[];
  defaultSizing?: ColumnSizingState;
  /**
   * When true (default), parent owns sort (server lists).
   * When false, table sorts rows client-side (detail ledgers).
   */
  manualSorting?: boolean;
  /** Controlled sort. Uncontrolled when omitted and manualSorting=false. */
  sorting?: SortingState;
  onSortingChange?: (next: SortingState) => void;
  getRowId?: (row: TData) => string;
  empty?: ReactNode;
  toolbarExtra?: ReactNode;
  /** Optional refresh — visible icon next to ⋯ (e.g. listQuery.refetch). */
  onRefresh?: () => void;
  /**
   * Ephemeral row highlight after filter-from-cell — plan/30+35.
   * Match against getRowId (doc id).
   */
  highlightedRowId?: string | null;
  className?: string;
};

function applyUpdater<T>(updater: Updater<T>, prev: T): T {
  return typeof updater === "function"
    ? (updater as (p: T) => T)(prev)
    : updater;
}

export function DataTable<TData extends RowData>(props: DataTableProps<TData>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const narrow = useContainerNarrow(rootRef);
  const stored = useMemo(
    () => loadDataTablePrefs(props.listId, props.companySlug),
    [props.listId, props.companySlug],
  );

  const [columnVisibility, setColumnVisibility] =
    useState<ColumnVisibilityState>(
      () => stored.columnVisibility ?? props.defaultVisibility ?? {},
    );
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    () => stored.columnOrder ?? props.defaultOrder ?? [],
  );
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    () => stored.columnSizing ?? props.defaultSizing ?? {},
  );
  const [viewMode, setViewMode] = useState<DataTableViewMode>(() =>
    resolveViewMode(stored),
  );
  const [showRelativeTime, setShowRelativeTime] = useState(() =>
    resolveShowRelativeTime(stored),
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [colsOpen, setColsOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [localSorting, setLocalSorting] = useState<SortingState>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const chromeHost = useDataTableChromeHost();
  const layoutHost = useDataTableLayoutHost();
  const filterReset = useDeskFilterReset();
  const manualSorting = props.manualSorting ?? true;
  const sortingControlled = props.sorting !== undefined;
  const sorting = sortingControlled ? (props.sorting ?? []) : localSorting;

  useEffect(() => {
    if (!menuOpen && !colsOpen && !filterMenuOpen) return;
    function onPointerDown(e: PointerEvent) {
      const el = menuRef.current;
      if (el && !el.contains(e.target as Node)) {
        setMenuOpen(false);
        setColsOpen(false);
        setFilterMenuOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setColsOpen(false);
        setFilterMenuOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, colsOpen, filterMenuOpen]);

  useEffect(() => {
    if (!props.highlightedRowId || !rootRef.current) return;
    const el = rootRef.current.querySelector('[data-flash="1"]');
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [props.highlightedRowId, props.data]);

  useEffect(() => {
    saveDataTablePrefs(props.listId, props.companySlug, {
      columnVisibility,
      columnOrder,
      columnSizing,
      viewMode,
      showRelativeTime,
    });
  }, [
    props.listId,
    props.companySlug,
    columnVisibility,
    columnOrder,
    columnSizing,
    viewMode,
    showRelativeTime,
  ]);

  const showTable =
    viewMode === "table" || (viewMode === "auto" && !narrow);

  const table = useTable({
    features: deskTableFeatures,
    data: props.data,
    columns: props.columns,
    getRowId: props.getRowId,
    manualSorting,
    columnResizeMode: "onChange",
    state: {
      columnVisibility,
      columnOrder,
      columnSizing,
      sorting,
    },
    onColumnVisibilityChange: (u) =>
      setColumnVisibility((prev) => applyUpdater(u, prev)),
    onColumnOrderChange: (u) => setColumnOrder((prev) => applyUpdater(u, prev)),
    onColumnSizingChange: (u) =>
      setColumnSizing((prev) => applyUpdater(u, prev)),
    onSortingChange: (u) => {
      const next = applyUpdater(u, sorting);
      if (props.onSortingChange) props.onSortingChange(next);
      if (!sortingControlled) setLocalSorting(next);
    },
  });

  const leafColumns = table.getAllLeafColumns();

  function resetPrefs() {
    clearDataTablePrefs(props.listId, props.companySlug);
    setColumnVisibility(props.defaultVisibility ?? {});
    setColumnOrder(props.defaultOrder ?? []);
    setColumnSizing(props.defaultSizing ?? {});
    setViewMode("auto");
    setShowRelativeTime(true);
  }

  function moveColumn(id: string, dir: -1 | 1) {
    const order =
      columnOrder.length > 0
        ? [...columnOrder]
        : leafColumns.map((c) => c.id);
    const i = order.indexOf(id);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    const [item] = next.splice(i, 1);
    next.splice(j, 0, item!);
    setColumnOrder(next);
  }

  const layoutToggle = (
    <div className="flex items-center gap-2">
      <label className="flex cursor-pointer items-center gap-1.5 text-xs text-[var(--color-muted)]">
        <input
          type="checkbox"
          className="size-3.5 accent-[var(--color-foreground)]"
          checked={showRelativeTime}
          onChange={(e) => setShowRelativeTime(e.target.checked)}
        />
        <span title="Show time ago under Date">Time ago</span>
      </label>
      <div
        className="inline-flex overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)]"
        role="group"
        aria-label="List layout"
      >
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center px-2 py-1.5 transition-colors",
            showTable
              ? "bg-[var(--color-border)]/55 text-[var(--color-foreground)]"
              : "bg-[var(--color-card)] text-[var(--color-muted)] hover:text-[var(--color-foreground)]",
          )}
          aria-label="Show as table"
          title="Show as table"
          aria-pressed={showTable}
          onClick={() => setViewMode("table")}
        >
          <Columns3 className="size-3.5" aria-hidden />
        </button>
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center border-l border-[var(--color-border)] px-2 py-1.5 transition-colors",
            !showTable
              ? "bg-[var(--color-border)]/55 text-[var(--color-foreground)]"
              : "bg-[var(--color-card)] text-[var(--color-muted)] hover:text-[var(--color-foreground)]",
          )}
          aria-label="Show stacked"
          title="Show stacked"
          aria-pressed={!showTable}
          onClick={() => setViewMode("stacked")}
        >
          <LayoutList className="size-3.5" aria-hidden />
        </button>
      </div>
    </div>
  );

  const filterActionsEnabled = Boolean(
    filterReset &&
      (filterReset.canResetToDefault || filterReset.canClearAll),
  );

  const overflowMenu = (
    <div ref={menuRef} className="relative flex items-center gap-1.5">
      {filterReset ? (
        <div className="relative">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="px-2.5"
            aria-label="Filter actions"
            title="Filters"
            aria-expanded={filterMenuOpen}
            aria-haspopup="menu"
            disabled={!filterActionsEnabled}
            onClick={() => {
              setFilterMenuOpen((o) => !o);
              setMenuOpen(false);
              setColsOpen(false);
            }}
          >
            <FilterX className="size-4" aria-hidden />
          </Button>
          {filterMenuOpen ? (
            <div
              className={cn(
                "absolute z-30 mt-1 min-w-[12.5rem] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-card)] p-1 shadow-[var(--shadow-soft)]",
                chromeHost ? "right-0 top-full" : "left-0 top-full",
              )}
              role="menu"
              aria-label="Filter actions"
            >
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)] disabled:opacity-40"
                disabled={!filterReset.canResetToDefault}
                onClick={() => {
                  filterReset.onResetToDefault();
                  setFilterMenuOpen(false);
                }}
              >
                Reset to default
              </button>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)] disabled:opacity-40"
                disabled={!filterReset.canClearAll}
                onClick={() => {
                  filterReset.onClearAll();
                  setFilterMenuOpen(false);
                }}
              >
                Clear all filters
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {props.onRefresh ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="px-2.5"
          aria-label="Refresh list"
          title="Refresh"
          onClick={() => props.onRefresh?.()}
        >
          <RefreshCw className="size-4" aria-hidden />
        </Button>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="px-2.5"
        aria-label="List actions"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={() => {
          setMenuOpen((o) => !o);
          setColsOpen(false);
          setFilterMenuOpen(false);
        }}
      >
        <MoreHorizontal className="size-4" aria-hidden />
      </Button>
      {menuOpen ? (
        <div
          className={cn(
            "absolute z-30 mt-1 min-w-[12.5rem] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-card)] p-1 shadow-[var(--shadow-soft)]",
            chromeHost ? "right-0 top-full" : "left-0 top-full",
          )}
          role="menu"
          aria-label="List actions"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)]"
            onClick={() => {
              setColsOpen(true);
              setMenuOpen(false);
            }}
          >
            <Columns3 className="size-3.5 shrink-0" aria-hidden />
            Columns
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)]"
            onClick={() => {
              resetPrefs();
              setMenuOpen(false);
            }}
          >
            <RotateCcw className="size-3.5 shrink-0" aria-hidden />
            Reset columns
          </button>
        </div>
      ) : null}
      {colsOpen ? (
        <div
          className={cn(
            "absolute z-30 mt-1 min-w-[14rem] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-card)] p-2 shadow-[var(--shadow-soft)]",
            chromeHost ? "right-0 top-full" : "left-0 top-full",
          )}
          role="dialog"
          aria-label="Choose columns"
        >
          <ul className="m-0 list-none space-y-1 p-0">
            {leafColumns.map((col) => {
              const canHide = col.getCanHide();
              const label =
                typeof col.columnDef.header === "string"
                  ? col.columnDef.header
                  : col.id;
              return (
                <li
                  key={col.id}
                  className="flex items-center gap-2 rounded-md px-1 py-1 text-sm"
                >
                  <label className="flex min-w-0 flex-1 items-center gap-2">
                    <input
                      type="checkbox"
                      checked={col.getIsVisible()}
                      disabled={!canHide}
                      onChange={col.getToggleVisibilityHandler()}
                    />
                    <span className="truncate">{label}</span>
                  </label>
                  <button
                    type="button"
                    className="text-xs text-[var(--color-muted)] hover:text-[var(--color-foreground)]"
                    aria-label={`Move ${label} earlier`}
                    onClick={() => moveColumn(col.id, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="text-xs text-[var(--color-muted)] hover:text-[var(--color-foreground)]"
                    aria-label={`Move ${label} later`}
                    onClick={() => moveColumn(col.id, 1)}
                  >
                    ↓
                  </button>
                </li>
              );
            })}
          </ul>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-2 w-full"
            onClick={() => {
              resetPrefs();
              setColsOpen(false);
            }}
          >
            <RotateCcw className="size-3.5" aria-hidden />
            Reset columns
          </Button>
        </div>
      ) : null}
      {props.toolbarExtra}
    </div>
  );

  return (
    <ShowRelativeTimeContext.Provider value={showRelativeTime}>
    <div ref={rootRef} className={cn("mt-4", props.className)}>
      {chromeHost
        ? createPortal(overflowMenu, chromeHost)
        : (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {overflowMenu}
            </div>
          )}
      {layoutHost
        ? createPortal(layoutToggle, layoutHost)
        : (
            <div className="mb-3 flex justify-end">{layoutToggle}</div>
          )}

      {props.data.length === 0 ? (
        (props.empty ?? null)
      ) : showTable ? (
        <div className="rounded-[var(--radius-xl)] border border-[var(--color-border)]/90 bg-[var(--color-card)] shadow-[var(--shadow-soft)]">
          {/*
            Fixed layout + size-share widths: card stays inside the desk
            column (filters/title align). No overflow-x wrapper — that makes
            a scroll containing block and kills page-scroll sticky thead.
          */}
          <Table className="w-full table-fixed">
            <TableHeader className="sticky top-[var(--desk-sticky-offset,0px)] z-10 bg-[var(--color-card)] shadow-[0_1px_0_var(--color-border)]">
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id} className="hover:bg-transparent">
                  {hg.headers.map((header) => {
                    const sortable = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    const align = columnAlign(header.column.columnDef.meta);
                    return (
                      <TableHead
                        key={header.id}
                        style={columnShareStyle(
                          header.getSize(),
                          table.getTotalSize(),
                        )}
                        className={cn(
                          "relative overflow-hidden bg-[var(--color-card)]",
                          alignClass(align),
                        )}
                      >
                        {header.isPlaceholder ? null : sortable ? (
                          <button
                            type="button"
                            className={cn(
                              "inline-flex items-center gap-1 hover:text-[var(--color-foreground)]",
                              align === "right" && "w-full justify-end",
                              align === "center" && "w-full justify-center",
                            )}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {sorted === "asc" ? (
                              <ArrowUp className="size-3.5" aria-hidden />
                            ) : sorted === "desc" ? (
                              <ArrowDown className="size-3.5" aria-hidden />
                            ) : (
                              <ArrowUpDown
                                className="size-3.5 opacity-40"
                                aria-hidden
                              />
                            )}
                          </button>
                        ) : (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )
                        )}
                        {header.column.getCanResize() ? (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={cn(
                              "absolute top-0 right-0 h-full w-1 cursor-col-resize touch-none select-none bg-transparent hover:bg-[var(--color-primary)]/40",
                              header.column.getIsResizing() &&
                                "bg-[var(--color-primary)]/60",
                            )}
                            aria-hidden
                          />
                        ) : null}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => {
                const flashing =
                  props.highlightedRowId != null &&
                  row.id === props.highlightedRowId;
                return (
                  <TableRow
                    key={row.id}
                    data-flash={flashing ? "1" : undefined}
                    className={
                      flashing
                        ? "animate-[listRowFlash_1.5s_ease-out]"
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={columnShareStyle(
                          cell.column.getSize(),
                          table.getTotalSize(),
                        )}
                        className={cn(
                          "overflow-hidden",
                          alignClass(columnAlign(cell.column.columnDef.meta)),
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <ul className="m-0 list-none divide-y divide-[var(--color-border)]/90 rounded-[var(--radius-xl)] border border-[var(--color-border)]/90 bg-[var(--color-card)] p-0 shadow-[var(--shadow-soft)]">
          {table.getRowModel().rows.map((row) => {
            const flashing =
              props.highlightedRowId != null &&
              row.id === props.highlightedRowId;
            return (
            <li
              key={row.id}
              data-flash={flashing ? "1" : undefined}
              className={cn(
                "space-y-2 px-4 py-[var(--desk-row-y)]",
                flashing && "animate-[listRowFlash_1.5s_ease-out]",
              )}
            >
              {row.getVisibleCells().map((cell) => {
                const label =
                  typeof cell.column.columnDef.header === "string"
                    ? cell.column.columnDef.header
                    : cell.column.id;
                const isActions = cell.column.id === "actions";
                return (
                  <div
                    key={cell.id}
                    className={cn(
                      "flex gap-3 text-sm",
                      isActions ? "justify-end pt-1" : "justify-between",
                    )}
                  >
                    {!isActions ? (
                      <span className="shrink-0 text-[var(--color-muted)]">
                        {label}
                      </span>
                    ) : null}
                    <div
                      className={cn(
                        "min-w-0",
                        isActions ? "" : "text-right font-medium",
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </div>
                  </div>
                );
              })}
            </li>
            );
          })}
        </ul>
      )}
    </div>
    </ShowRelativeTimeContext.Provider>
  );
}
